<template>
  <tr class="">
    <td class="main-td">
      <RouterLink :to="goToVacancy" class="vacancy-name-link d-inline body-2">
        {{ vacancy.title }}
      </RouterLink>
      <VacancyLockInfo v-if="vacancy.visibility === vacancyVisibility.INVITATION" />
      <a
        :href="externalUrl"
        target="_blank"
        class="text-decoration-none"
        style="vertical-align: top"
      >
        <VIcon v-if="isActive" small color="primary">
          mdi-open-in-new
        </VIcon>
      </a>
      <div v-if="isDraft && !isAiInterviewCompiling" class="caption pt-1">
        <template v-if="!vacancy.completed">
          <br />
          <router-link
            :to="goToVacancy"
            style="color: #1fc57f; text-decoration: underline"
            v-if="!vacancy.completed"
          >
            {{ $t('setUpScenario') }}
          </router-link>
        </template>
        <template v-else>
          <span class="red--text">{{ $t('draft') }}</span>
          <span class="ml-2">{{ $t('saved') }}: {{ createdAt }}</span>
        </template>
      </div>
      <div v-if="isAiInterviewCompiling" style="color: #1fc57f;" class="caption pt-1">
        {{ $t('processingByAi') }}
      </div>
    </td>
    <td>
      <span class="d-md-none">{{ headers[1].text }}: </span>{{ creatorName }}
    </td>
    <td>
      <span class="d-md-none">{{ headers[2].text }}: </span>{{ city }}
    </td>
    <td class="candidates-td">
      <template v-if="isActive">
        <div>
          <RouterLink
            :to="{ name: 'candidates', params: { id: vacancy.id } }"
            class="text-decoration-none"
          >
            <span class="d-md-none">{{ headers[3].text }}: </span>{{ vacancy.counts.candidates }}
          </RouterLink>
        </div>
        <!--<RouterLink
          :to="{name: 'candidates', params: { id: vacancy.id }}"
          class="caption hover-show text-decoration-none d-none d-lg-block"
        >
          {{ $t('watchCandidates') }}
        </RouterLink>-->
      </template>
      <div v-if="!isActive" class="height-holder" />
    </td>
    <td>
      <template v-if="isActive">
        <div>
          <span class="d-md-none">{{ headers[4].text }}: </span>{{ vacancy.counts.interviews }}
          <span v-if="vacancy.counts.interviewsNew" class="red--text">
            (+{{ vacancy.counts.interviewsNew }})
          </span>
        </div>
      </template>
    </td>
    <td>
      <span class="d-md-none">{{ headers[5].text }}: </span>{{ hasTestText }}
    </td>
    <td>
      <template v-if="isActive">
        <span class="d-md-none">{{ headers[6].text }}: </span>
        <DatePicker
          v-if="publishedTill"
          v-model="datePicker"
          :date="new Date(vacancy.publishedTill).toISOString().substr(0, 10)"
          :loading="updateLoading"
          :min="tomorrow"
          @change="updatePublishedTill"
        >
          <template #activator="{ on: { click } }">
            <span class="pseudo-link" @click="click">{{ publishedTill }}</span>
          </template>
        </DatePicker>
      </template>
    </td>
    <td class="text-right menu-td">
      <VProgressCircular
        v-if="actionLoading"
        indeterminate
        color="blue"
        :size="30"
        :width="3"
      />
      <div v-else>
        <!--<VBtn
          v-if="isDraft"
          small
          text
          icon
          @click="destroy"
        >
          <VIcon>
            mdi-trash-can
          </VIcon>
        </VBtn>-->
        <VMenu class="table-menu" bottom left>
          <template v-slot:activator="{ on, attrs }">
            <VBtn icon v-bind="attrs" v-on="on">
              <VIcon color="#757575">
                mdi-dots-vertical
              </VIcon>
            </VBtn>
          </template>

          <VList class="table-list">
            <VListItem v-if="!isDraft" class="table-list-item">
              <a
                href="#"
                @click.prevent="archive"
                :class="{'disabled-link': isDisabledLink}"
                :aria-disabled="isDisabledLink"
              >
                {{ $t('archive') }}
              </a>
            </VListItem>
            <VListItem class="table-list-item">
              <a
                href="#"
                @click.prevent="clone"
                :class="{'disabled-link': isDisabledLink}"
                :aria-disabled="isDisabledLink"
              >
                {{ $t('copy') }}
              </a>
            </VListItem>
            <VListItem class="table-list-item" v-if="isDraft">
              <a
                href="#"
                @click.prevent="destroy"
                :class="{'disabled-link': isDisabledLink}"
                :aria-disabled="isDisabledLink"
              >
                {{ $t('delete') }}
              </a>
            </VListItem>
          </VList>
        </VMenu>
      </div>
    </td>
  </tr>
</template>

<script>
import addDays from 'date-fns/addDays';
import VacancyLockInfo from '@/components/Vacancy/VacancyLockInfo';
import {
  vacancyVisibility, vacancyStatus, wizardSteps,
} from '@/enums';
import RouterController from '@/orm/models/Wizard/RouterController';
import useUser from '@/compositions/useUser';
import useSubscription from '@/compositions/accounting/useSubscription';
import { DATE_FORMATS, format } from '../../date-utils';
import ValidationMixin from '../../mixins/ValidationMixin';
import { safeGet } from '../../functions';
import DatePicker from '../Input/DatePicker.vue';
import Vacancy from '../../orm/models/Vacancy/Vacancy';

export default {
  name: 'VacancyTableRow',
  components: { VacancyLockInfo, DatePicker },
  mixins: [ValidationMixin],
  props: {
    company: {
      type: Object,
      required: true,
    },
    vacancy: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      required: true,
    },
    limitedVacancies: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  setup() {
    const { getUsers } = useUser();
    const { isExpired } = useSubscription();

    return {
      getUsers,
      isExpired,
    };
  },
  data() {
    return {
      datePicker: false,
      tomorrow: addDays(new Date(), 1)
        .toISOString()
        .substr(0, 10),
      updateLoading: false,
      actionLoading: false,
      vacancyVisibility,
    };
  },
  computed: {
    createdAt() {
      return format(new Date(this.vacancy.createdAt), DATE_FORMATS[this.$i18n.locale].short);
    },
    publishedTill() {
      return this.vacancy.publishedTill
        ? format(new Date(this.vacancy.publishedTill), DATE_FORMATS[this.$i18n.locale].short)
        : null;
    },
    hasTestText() {
      return this.vacancy.hasTest ? this.$t('yes') : this.$t('no');
    },
    city() {
      return safeGet(this.vacancy.address, ['city']);
    },
    creatorName() {
      return safeGet(this.vacancy, ['creatorName']);
    },
    isDraft() {
      return this.vacancy.status === vacancyStatus.DRAFT;
    },
    isActive() {
      return this.vacancy.status === vacancyStatus.ACTIVE
        && !this.vacancy?.isAiInterviewCompiling
        && !this.vacancy?.isAiInterviewRecompiling;
    },
    isAiInterviewCompiling() {
      return this.vacancy.isAiInterviewCompiling || this.vacancy.isAiInterviewRecompiling;
    },
    externalUrl() {
      return `${process.env.VUE_APP_FRONT_URL}/v/${this.vacancy.id}`;
    },
    isDisabledLink() {
      return this.vacancy.isAiInterviewCompiling
        || this.vacancy.isAiInterviewRecompiling
        || this.vacancy.aiQuestionsGenerating;
    },
    isCompletedVacancyRoute() {
      return this.vacancy.completed
        ? { name: this.isActive ? 'candidates' : 'vacancy', params: { id: this.vacancy.id } }
        : null;
    },
    isAiQuestionsRoute() {
      const { wizardState } = this.vacancy;
      if (
        this.vacancy.aiQuestionsGenerated
        && !this.vacancy.aiQuestionsGenerating
        && !this.vacancy.isAiInterviewCompiled
        && !this.vacancy.publishAfterAiCompile
        && wizardState?.isAiInterviewer
        && wizardState.step !== wizardSteps.COMMON
      ) {
        return {
          name: 'vacancy-ai-questions',
          params: { vacancyId: this.vacancy.id },
        };
      }
      return null;
    },
    isAiQuestionsEditRoute() {
      if (
        this.vacancy.aiQuestionsGenerated
        && this.vacancy.isAiInterviewCompiled
        && this.vacancy.isAiInterviewRecompiling
      ) {
        return {
          name: 'vacancy-ai-questions-edit',
          params: { vacancyId: this.vacancy.id },
        };
      }
      return null;
    },
    isPresetQuestionsRoute() {
      const { wizardState } = this.vacancy;
      if (
        wizardState?.presetId
        && wizardState?.step === 'preset'
        && !wizardState?.isCompleted
        && !wizardState?.isAiInterviewer
        && wizardState?.interviewerId
      ) {
        return {
          name: 'vacancy-questions',
          params: {
            vacancyId: this.vacancy.id,
            interviewerId: wizardState.interviewerId,
          },
        };
      }
      return null;
    },
    defaultWizardRoute() {
      const { wizardState } = this.vacancy;
      const step = wizardState?.step || wizardSteps.AI_QUESTIONS;

      const controller = new RouterController({
        step,
        vacancy: this.vacancy,
      });
      controller.initRoutes();

      return controller.makeRoute({
        step,
        wizardState,
        overloadParams: { vacancyId: this.vacancy.id },
      }) || {
        name: 'vacancy-preset',
        params: { vacancyId: this.vacancy.id },
      };
    },
    goToVacancy() {
      return (
        this.isCompletedVacancyRoute
        || this.isAiQuestionsRoute
        || this.isAiQuestionsEditRoute
        || this.isPresetQuestionsRoute
        || this.defaultWizardRoute
      );
    },
  },
  methods: {
    async destroy() {
      await this.withApiErrorHandling(
        async () => {
          await Vacancy.api().destroy(this.vacancy.id);
          this.$toast.info(this.$t('vacancyDeleted'));
          await Vacancy.delete(this.vacancy.id);
        },
        (e) => this.$toast.error(e.message),
        'actionLoading',
      );
    },
    async archive() {
      const beforeDestroy = () => {
        this.$toast.info(this.$t('vacancyArchived'));
      };
      await this.withApiErrorHandling(
        async () => {
          this.$on('hook:beforeDestroy', beforeDestroy);
          await Vacancy.api().archive(this.vacancy.id);
          await this.getUsers();
        },
        (e) => {
          this.$off('hook:beforeDestroy', beforeDestroy);
          this.$toast.error(e.message);
        },
        'actionLoading',
      );
    },
    async clone() {
      if (this.isExpired) {
        this.$emit('openDialogExpiredTariff');
        return;
      }
      await this.withApiErrorHandling(
        async () => {
          if (this.limitedVacancies) {
            this.$emit('openDialogMaximumVacancies', 'clone');
            return;
          }
          await Vacancy.api().clone(this.vacancy.id);
          this.$toast.info(this.$t('vacancyCopied'));
        },
        (e) => this.$toast.error(e.message),
        'actionLoading',
      );
    },
    async updatePublishedTill(date) {
      await this.withApiErrorHandling(
        async () => {
          await Vacancy.api().update(this.vacancy.id, { publishedTill: date });
          this.$toast.success(this.$t('dateChangeSuccessMessage'));
          this.datePicker = false;
        },
        (error) => {
          if (error.errors && error.errors.publishedTill) {
            this.$toast.error(error.errors.publishedTill.join(', '));
          } else {
            this.$toast.error(error.message);
          }
        },
        'updateLoading',
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@media screen and (max-width: 1024px) {
  .v-data-table tr {
    height: 100%;
  }
}

.disabled-link {
  color: #ccc;
  pointer-events: none;
  cursor: not-allowed;
}
</style>

<i18n locale="en" lang="yaml">
  draft: Draft
  saved: Saved
  archive: Archive
  copy: Copy
  delete: Delete
  vacancyDeleted: Job deleted
  vacancyArchived: Job archived
  vacancyCopied: Job copied
  dateChangeSuccessMessage: Job publication period was changed
  setUpScenario: Set up interview scenario
  processingByAi: Processing by AI
  yes: Yes
  no: No
</i18n>

<i18n locale="ru" lang="yaml">
  draft: Черновик
  saved: Сохранён
  archive: В архив
  copy: Создать копию
  delete: Удалить
  vacancyDeleted: Вакансия удалена
  vacancyArchived: Вакансия перемещена в архив
  vacancyCopied: Вакансия скопирована
  dateChangeSuccessMessage: Срок размещения вакансии обновлён
  setUpScenario: Настроить сценарий собеседования
  processingByAi: В процессе обработки от ИИ
  yes: Да
  no: Нет
</i18n>
